<script setup>
import { computed, ref } from 'vue';
import SelectField from './SelectField.vue';
import { useNearestLocations, NEARBY_LOCATING_MESSAGE } from './composables/useNearestLocations.js';

const props = defineProps({
    locations: {
        type: Array,
        required: true,
    },
    nearestLocationsUrl: {
        type: String,
        required: true,
    },
    modelValue: {
        type: [Number, null],
        default: null,
    },
    // Passed straight to SelectField - see its own note. The label itself stays "Where" either
    // way, so a screen reader hears the same thing wherever this dropdown is used.
    hideLabel: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

/*
 * The value of the mobile "Nearby" option.
 *
 * Deliberately a non-numeric string. It can never collide with a location id, and if it ever
 * escaped the guard in onSelectChange() the Number() coercion there would turn it into null
 * rather than a plausible-looking spa. It must never be emitted: SearchBar re-runs the search
 * on every form change on the results page, and LocationSwitcher writes a cookie and reloads,
 * so a sentinel reaching either one means a bogus search or a wrong reload.
 */
const NEARBY_VALUE = '__nearby__';

/*
 * U+2197 NORTH EAST ARROW followed by U+FE0E VARIATION SELECTOR-15.
 *
 * Written as escapes rather than pasted in, because VS15 is zero-width and invisible in an
 * editor: pasted, it reads as a stray space and gets tidied away by the next person through,
 * silently taking the monochrome rendering with it. It is the selector that stops iOS
 * promoting the arrow to a full-colour emoji tile, which would sit far heavier than the
 * label beside it.
 *
 * Decoration is the most a native picker allows. Apple's own location-services arrow is an
 * SF Symbol rather than a codepoint, so it cannot be reached from a web page, and an <option>
 * takes no markup, no CSS and no images - a character is the only lever there is.
 */
const NEARBY_ARROW = '↗︎';

const options = computed(() => props.locations.map((location) => ({
    value: location.id,
    label: location.name,
})));

const {
    status,
    reason,
    message,
    locations: nearestLocations,
    permissionGranted,
    fetchNearest,
    reset,
} = useNearestLocations(props.nearestLocationsUrl);

const selectFieldRef = ref(null);
let hasAutoFetched = false;

/*
 * True while a lookup started from the MOBILE option is running, and after one has failed.
 *
 * This is what lets the closed field report progress and failure without a single new element
 * on the page: while it is set, displayValue below hands SelectField the sentinel instead of
 * the real value, so the native <select> renders the Nearby option's label - "Finding your
 * nearest…", then the error - as the field's own value.
 *
 * It is gated on how the lookup STARTED, not on status alone, and that distinction matters.
 * displayValue reaches SelectField at every width, and a sentinel it cannot find in `options`
 * leaves SelectField.selectedOption null. Were this keyed off status, a desktop click on the
 * popover's Nearby row would blank the trigger to "Select location" while locating and leave
 * it blank after a failure. Only the native path ever sets it.
 */
const nativeNearbyPending = ref(false);

const isBusy = computed(() => status.value === 'locating' || status.value === 'loading');
const hasNearest = computed(() => status.value === 'ready' && nearestLocations.value.length > 0);

const displayValue = computed(() => (nativeNearbyPending.value ? NEARBY_VALUE : props.modelValue));

// What the mobile option says. It is one option throughout, relabelled, so the customer's
// tap target does not move under them between states.
//
// Only the resting label carries the arrow. The other two states are read as sentences rather
// than as a control to press, and the busy and failed labels also become the CLOSED field's
// own value (see displayValue), where a leading arrow would look like part of the spa name.
const nearbyOptionLabel = computed(() => {
    if (isBusy.value) {
        return NEARBY_LOCATING_MESSAGE;
    }

    return message.value ?? `${NEARBY_ARROW} Find Nearby`;
});

/*
 * Runs a lookup and, on success, picks the closest spa - the one tap the mobile control is
 * built around.
 *
 * This function and selectNearest() are the ONLY places that emit after a lookup, and both are
 * reached solely from something the customer did. Note that the id emitted here comes from the
 * promise THIS call returned, not from the shared nearestLocations ref. That is the guard: an
 * automatic lookup can write those refs all it likes and still never reach an emit. For the
 * same reason there is deliberately no watcher on status or on nearestLocations anywhere in
 * this component - a watcher is exactly the hole an auto-fetch would drive a selection through.
 */
async function startNativeNearby() {
    nativeNearbyPending.value = true;
    hasAutoFetched = true;

    const nearest = await fetchNearest();

    if (! nearest.length) {
        // Left pending on purpose, so the field keeps showing why it failed. Cleared by the
        // customer's next real choice of spa, in onSelectChange().
        hasAutoFetched = false;

        return;
    }

    nativeNearbyPending.value = false;
    emit('update:modelValue', nearest[0].id);
}

function onSelectChange(value) {
    if (value === NEARBY_VALUE) {
        startNativeNearby();

        return;
    }

    nativeNearbyPending.value = false;

    /*
     * A real choice made while the lookup is in a failed or empty state clears it, so the
     * option reads "Find Nearby" again next time rather than still showing a stale error.
     *
     * This is the only moment available to do it on mobile: once the sentinel is the selected
     * option, tapping it again changes nothing and fires no event. A successful lookup is
     * deliberately left alone - desktop keeps its three results rows visible after a pick, and
     * that behaviour is unchanged.
     */
    if (status.value === 'error' || reason.value === 'empty') {
        reset();
        hasAutoFetched = false;
    }

    emit('update:modelValue', Number(value) || null);
}

// Desktop only: the popover opening is the trigger. Emits nothing, by design - this fires
// without the customer asking for it, and a selection made here would overwrite a spa seeded
// from their cookie, which in LocationSwitcher means an unrequested page reload.
async function onOpen() {
    if (! permissionGranted.value || hasAutoFetched) {
        return;
    }

    hasAutoFetched = true;

    if (! (await fetchNearest()).length) {
        hasAutoFetched = false;
    }
}

async function onNearbyClick() {
    hasAutoFetched = true;

    // Released on failure so reopening the popover can try again. Without this a single
    // failed lookup would lock the automatic path out for the rest of the page's life.
    if (! (await fetchNearest()).length) {
        hasAutoFetched = false;
    }
}

function selectNearest(location) {
    emit('update:modelValue', location.id);
    selectFieldRef.value?.close();
}
</script>

<template>
    <SelectField
        ref="selectFieldRef"
        name="location"
        label="Where"
        :hide-label="hideLabel"
        placeholder="Select location"
        bold-placeholder
        :options="options"
        :model-value="displayValue"
        @update:model-value="onSelectChange"
        @open="onOpen"
    >
        <!-- Forwarded so a caller can reach inside the trigger without knowing SelectField
             is in here. -->
        <template #trigger-suffix>
            <slot name="trigger-suffix" />
        </template>

        <!--
            The mobile half of this control. Below 'medium' the popover below is display:none
            and the native <select> is the whole field, so the lookup has to be offered as an
            option inside it. No breakpoint logic is needed for that: the <select> is already
            mobile-only, which makes this option mobile-only too, and it costs nothing on
            desktop where the whole element is hidden.
        -->
        <template #before-native-options>
            <!-- The group is rendered in every state, not just once results arrive, and it
                 earns its keep twice over. It is what draws the heading and the rule that
                 separate this section from the alphabetical list - both are the OS's own
                 optgroup chrome, so asking for the group is the whole of getting them, and
                 neither can be styled into a bare <option>. Keeping it constant also means
                 the list does not grow a heading out of nowhere when the lookup lands: the
                 section is already there, and only its contents change. -->
            <optgroup label="Nearest to you">
                <template v-if="hasNearest">
                    <option v-for="location in nearestLocations" :key="location.id" :value="location.id">
                        {{ location.name }} ({{ location.distance }} miles)
                    </option>
                </template>
                <option v-else :value="NEARBY_VALUE">{{ nearbyOptionLabel }}</option>
            </optgroup>
        </template>

        <template #before-options>
            <div v-if="isBusy" class="select_field_option nearby_option is_loading">
                Finding your nearest locations…
            </div>
            <template v-else-if="hasNearest">
                <div
                    v-for="location in nearestLocations"
                    :key="location.id"
                    class="select_field_option nearby_option"
                    role="option"
                    @click="selectNearest(location)"
                >
                    <span class="nearby_option_name">{{ location.name }}</span>
                    <span class="nearby_option_distance">{{ location.distance }} miles away</span>
                </div>
            </template>
            <!-- A failure, or a lookup that honestly found nothing. Before this existed every
                 one of these silently re-rendered the word "Nearby", which is what made the
                 feature look broken. The retry row stays below the message so someone who has
                 just turned location services back on has a way in without reloading. -->
            <template v-else-if="message">
                <div class="select_field_option nearby_option is_message">{{ message }}</div>
                <div class="select_field_option nearby_option" @click="onNearbyClick">
                    Try again
                </div>
            </template>
            <div v-else class="select_field_option nearby_option" @click="onNearbyClick">
                Nearby
            </div>
            <div class="nearby_divider"></div>
        </template>
    </SelectField>
</template>
